import React, { useState } from "react";
import { usePoints } from "../context/PointsContext";
import { useAuth } from "../context/AuthContext";
import {
  ArrowDownRight,
  ArrowUpRight,
  PlusCircle,
  Info,
  Zap,
  ShieldCheck,
  Mail,
  Youtube,
  CreditCard,
} from "lucide-react";
import { COLORS } from "../utils/constants";
import { translations, LanguageCode } from "../utils/translations";

type PointsHistoryT = (typeof translations)["en"]["pointsHistory"];

const renderAccessDenied = (t: PointsHistoryT) => (
  <div style={{ padding: "80px 20px", textAlign: "center", minHeight: "60vh" }}>
    <div
      style={{
        width: "60px",
        height: "60px",
        background: "#F3F4F6",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 20px",
        color: "#9CA3AF",
      }}
    >
      <ShieldCheck size={32} />
    </div>
    <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1F2937" }}>
      {t.accessDenied}
    </h2>
    <p style={{ color: "#6B7280", marginTop: "8px" }}>
      {t.accessDeniedBody}
    </p>
  </div>
);

interface PointsHistoryProps {
  lang?: LanguageCode;
}

const PointsHistory: React.FC<PointsHistoryProps> = ({ lang = "en" }) => {
  const t = translations[lang].pointsHistory;
  const { user } = useAuth();
  const { points, transactions, addPoints } = usePoints();
  const [topUpAmount, setTopUpAmount] = useState<string>("100");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const downloadCSVFile = (data: any[], fileName: string = "data.csv") => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);

    const csvRows = [
      headers.join(","), // header row
      ...data.map((row) =>
        headers
          .map((field) => {
            const value = row[field];

            // handle commas & quotes safely
            if (typeof value === "string") {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join(",")
      ),
    ];

    const csvContent = csvRows.join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const formatTransactionsForCSV = (data: typeof transactions) => {
    return data.map((tx) => ({
      Date: new Date(tx.date).toLocaleString(),
      Description: tx.description,
      Type: tx.type,
      Amount: tx.amount,
    }));
  };
  const getLast6MonthsTransactions = () => {
    const now = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(now.getMonth() - 6);

    return transactions.filter((tx) => new Date(tx.date) >= sixMonthsAgo);
  };
  const renderTransactionHistory = () => (
    <div style={{ flex: 2 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: 800,
            color: "#111827",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <CreditCard size={24} style={{ color: COLORS.brandPurple }} />
          {t.transactionHistory}
        </h2>

        {/* ✅ CSV Buttons */}
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() =>
              downloadCSVFile(
                formatTransactionsForCSV(getLast6MonthsTransactions()),
                "transactions-6-months.csv"
              )
            }
            style={{
              padding: "8px 14px",
              borderRadius: "8px",
              border: "1px solid #D1D5DB",
              background: "#fff",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            {t.sixMonths}
          </button>

          <button
            onClick={() =>
              downloadCSVFile(
                formatTransactionsForCSV(transactions),
                "all-transactions.csv"
              )
            }
            style={{
              padding: "8px 14px",
              borderRadius: "8px",
              border: "1px solid #D1D5DB",
              background: "#fff",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            {t.all}
          </button>
        </div>
      </div>

      {transactions.length === 0 ? (
        <div
          style={{
            background: "#F9FAFB",
            padding: "60px 20px",
            borderRadius: "24px",
            textAlign: "center",
            border: "1px dashed #D1D5DB",
          }}
        >
          <p style={{ color: "#9CA3AF" }}>{t.noTransactions}</p>
        </div>
      ) : (
        <div
          style={{
            background: "white",
            borderRadius: "24px",
            border: "1px solid #E5E7EB",
            overflow: "hidden",
          }}
        >
          {transactions.map((tx, index) => (
            <div
              key={tx.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "20px",
                borderBottom:
                  index < transactions.length - 1
                    ? "1px solid #F3F4F6"
                    : "none",
                alignItems: "center",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#F9FAFB")
              }
              onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
            >
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "12px",
                    background: tx.type === "earned" ? "#ECFDF5" : "#FEF2F2",
                    color: tx.type === "earned" ? "#10B981" : "#EF4444",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {tx.type === "earned" ? (
                    <ArrowUpRight size={20} />
                  ) : (
                    <ArrowDownRight size={20} />
                  )}
                </div>

                <div>
                  <div
                    style={{
                      fontWeight: "700",
                      color: "#374151",
                      fontSize: "1.05rem",
                    }}
                  >
                    {tx.description}
                  </div>

                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: "#9CA3AF",
                      marginTop: "2px",
                    }}
                  >
                    {new Date(tx.date).toLocaleDateString()} {t.at}{" "}
                    {new Date(tx.date).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              </div>

              <div
                style={{
                  fontWeight: "800",
                  fontSize: "1.1rem",
                  color: tx.type === "earned" ? "#10B981" : "#EF4444",
                }}
              >
                {tx.type === "earned" ? "+" : "-"}
                {tx.amount.toFixed(0)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleAddPoints = async () => {
    const amount = parseFloat(topUpAmount);
    if (isNaN(amount) || amount <= 0) {
      alert(t.invalidAmountAlert);
      return;
    }

    setIsSubmitting(true);
    try {
      await addPoints(amount, t.manualTopUp);
      alert(t.addedAlert.replace("{amount}", String(amount)));
    } catch (error) {
      alert(t.addFailedAlert);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return renderAccessDenied(t);
  const renderTopSection = () => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "24px",
        marginBottom: "40px",
      }}
    >
      {/* Balance Card */}
      <div
        style={{
          flex: "1 1 300px",
          background: COLORS.brandPurple,
          padding: "32px",
          borderRadius: "24px",
          color: "#fff",
          boxShadow: "0 20px 40px rgba(113,75,103,0.15)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "relative", zIndex: 1 }}>
          <p
            style={{
              fontSize: "0.9rem",
              opacity: 0.8,
              fontWeight: 600,
              marginBottom: "8px",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            {t.currentBalance}
          </p>
          <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            <span style={{ fontSize: "3.5rem", fontWeight: 900 }}>
              {points.toFixed(0)}
            </span>
            <span
              style={{ fontSize: "1.25rem", opacity: 0.8, fontWeight: 700 }}
            >
              {t.socialPoints}
            </span>
          </div>
          <p style={{ marginTop: "16px", fontSize: "0.95rem", opacity: 0.9 }}>
            {t.valueApprox.replace("{amount}", (points * 0.5).toFixed(2))}
          </p>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: "-20px",
            right: "-20px",
            fontSize: "12rem",
            opacity: 0.1,
            color: "#fff",
            transform: "rotate(-15deg)",
          }}
        >
          🪙
        </div>
      </div>

      {/* Top Up Card */}
      <div
        style={{
          flex: "1 1 350px",
          background: "#fff",
          padding: "32px",
          borderRadius: "24px",
          border: "1px solid #E5E7EB",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 700,
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#1F2937",
          }}
        >
          <PlusCircle size={20} style={{ color: COLORS.brandTeal }} />
          {t.topUp}
        </h2>

        <p
          style={{
            color: "#6B7280",
            fontSize: "0.9rem",
            marginBottom: "24px",
            lineHeight: "1.5",
          }}
        >
          {t.topUpBody}
        </p>

        <div style={{ display: "flex", gap: "10px", flexDirection: "column" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <span
              style={{
                position: "absolute",
                left: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9CA3AF",
                fontWeight: 600,
              }}
            >
              🪙
            </span>

            <input
              type="number"
              value={topUpAmount}
              onChange={(e) => setTopUpAmount(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 12px 12px 35px",
                borderRadius: "12px",
                border: "1px solid #D1D5DB",
                fontSize: "1rem",
                fontWeight: 600,
                outline: "none",
              }}
            />
          </div>

          <button
            onClick={handleAddPoints}
            disabled={isSubmitting}
            style={{
              backgroundColor: isSubmitting ? "#E5E7EB" : COLORS.brandTeal,
              color: "white",
              padding: "12px 24px",
              borderRadius: "12px",
              border: "none",
              fontWeight: 700,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              whiteSpace: "nowrap",
              minWidth: "90px",
            }}
          >
            {t.add}
          </button>
        </div>
      </div>
    </div>
  );
  return (
    <div
      style={{
        padding: "40px 24px",
        maxWidth: "1000px",
        margin: "0 auto",
        minHeight: "80vh",
        fontFamily: '"Inter", sans-serif',
      }}
    >
      {renderTopSection()}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "40px",
        }}
      >
        {/* Transaction History */}

        {renderTransactionHistory()}
        {/* Usage Guide */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              background: "#F8F9FF",
              padding: "32px",
              borderRadius: "24px",
              border: "1px solid #EEF1FF",
            }}
          >
            <h2
              style={{
                fontSize: "1.25rem",
                fontWeight: 700,
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: COLORS.brandPurple,
              }}
            >
              <Zap size={20} /> {t.howToUse}
            </h2>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              {[
                {
                  icon: <ShieldCheck size={18} />,
                  title: t.usageItems[0].title,
                  cost: t.usageItems[0].cost,
                },
                {
                  icon: <Youtube size={18} />,
                  title: t.usageItems[1].title,
                  cost: t.usageItems[1].cost,
                },
                {
                  icon: <Zap size={18} />,
                  title: t.usageItems[2].title,
                  cost: t.usageItems[2].cost,
                },
                {
                  icon: <Mail size={18} />,
                  title: t.usageItems[3].title,
                  cost: t.usageItems[3].cost,
                },
              ].map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <div style={{ color: COLORS.brandTeal, marginTop: "2px" }}>
                    {item.icon}
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        color: "#374151",
                      }}
                    >
                      {item.title}
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#6B7280" }}>
                      {item.cost}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: "32px",
                padding: "16px",
                background: "#fff",
                borderRadius: "16px",
                display: "flex",
                gap: "10px",
                alignItems: "flex-start",
              }}
            >
              <Info
                size={18}
                style={{ color: COLORS.brandTeal, flexShrink: 0 }}
              />
              <p
                style={{
                  fontSize: "0.85rem",
                  color: "#6B7280",
                  margin: 0,
                  lineHeight: "1.5",
                }}
              >
                {t.footerNote}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointsHistory;
